import React from "react";
import { Link } from "react-router-dom";
import { SectionHeader } from "@/components/shared/Common";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Target, Users2, HandCoins, Lock, Sparkles, ChevronRight } from "lucide-react";
import ReadMore from "@/components/shared/ReadMore";

export default function About() {
  return (
    <div>
      {/* Hero band */}
      <section className="bg-navy-900 text-white relative overflow-hidden">
        <div className="grain" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white px-3 py-1 rounded-full text-xs uppercase tracking-widest font-outfit font-bold mb-6 backdrop-blur" data-testid="about-mission-badge">
            <ShieldCheck className="w-3.5 h-3.5" /> Built by Security Professionals · Designed for the Security Industry
          </div>
          <h1 className="text-4xl sm:text-5xl font-outfit font-bold tracking-tight leading-tight">
            We&apos;re security operators — still working the job — who built a fairer platform for our industry.
          </h1>
          <ReadMore
            className="mt-6 text-lg max-w-3xl !text-slate-300"
            preview="Not a tech company that discovered security. Operators who empathise with the role because we still work it."
            more="Every feature — vetting, negotiation rooms, contracts, deployment confirmation, reliability tracking — comes from real experience on the door, the site and the principal. We built SecuSource low-cost on purpose: to improve standards across the industry, create better opportunities for professionals, and give companies a transparent, trusted way to reach the right people."
            buttonMoreLabel="Read the full story"
            testId="about-hero-readmore"
          />
        </div>
      </section>

      {/* Positioning */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <SectionHeader
          eyebrow="What SecuSource Is"
          title="A workforce platform. Not a recruitment website."
          subtitle="SecuSource helps security companies quickly find, verify, book and manage vetted security professionals and operational teams. Every part of the platform is designed around how security work actually happens — from PSA licence verification to SOP acknowledgements to booking-time contact unlock."
        />

        <div className="grid md:grid-cols-3 gap-4 mt-8">
          {[
            { icon: Target, title: "Our Mission", body: "Improve standards across the security industry by giving vetted professionals a serious platform, and giving hiring companies a faster, safer way to build the right team." },
            { icon: ShieldCheck, title: "Our Standards", body: "Verified PSA licences, real endorsements from real bookings, and separated ranking categories so CPOs and general guards are never mixed." },
            { icon: Users2, title: "Our Community", body: "Self-employed professionals, cluster team leads and operational companies — working together in one transparent, trusted environment." },
          ].map((c) => (
            <details key={c.title} className="card-flat p-6 group cursor-pointer">
              <summary className="list-none">
                <div className="w-11 h-11 rounded-md bg-brand-light text-brand flex items-center justify-center mb-4">
                  <c.icon className="w-5 h-5" />
                </div>
                <div className="font-outfit font-semibold text-white text-lg mb-1 flex items-center justify-between">{c.title} <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform text-brand" /></div>
              </summary>
              <p className="text-sm text-white/70 font-manrope leading-relaxed mt-2">{c.body}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Fair economics */}
      <section className="bg-[#0A0A0A] border-y border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
          <SectionHeader
            eyebrow="Fair Economics"
            title="Professionals keep their rate. Companies pay a transparent introduction fee."
          />

          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <div className="rounded-xl p-6 border border-gold-200 bg-gold-50" data-testid="about-pro-terms">
              <div className="text-xs uppercase tracking-widest text-gold-600 font-outfit font-bold mb-3 flex items-center gap-2"><HandCoins className="w-3.5 h-3.5" /> For Security Professionals</div>
              <ul className="space-y-3 text-sm font-manrope text-white">
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-gold-500 flex-shrink-0 mt-0.5" /><span>Security professionals are <strong>never charged</strong> to join SecuSource.</span></li>
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-gold-500 flex-shrink-0 mt-0.5" /><span>Registration is <strong>completely free</strong>.</span></li>
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-gold-500 flex-shrink-0 mt-0.5" /><span>SecuSource <strong>does not deduct commission</strong> from a security professional&apos;s hourly rate.</span></li>
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-gold-500 flex-shrink-0 mt-0.5" /><span>Professionals <strong>negotiate their own rates directly</strong> with the hiring company — receiving fair value for their qualifications, experience and time.</span></li>
              </ul>
            </div>

            <div className="rounded-xl p-6 border border-white/10 bg-[#0E0E0E]" data-testid="about-company-terms">
              <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-3 flex items-center gap-2"><Lock className="w-3.5 h-3.5" /> For Hiring Companies</div>
              <ul className="space-y-3 text-sm font-manrope text-white">
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" /><span>SecuSource earns revenue through a <strong>transparent introduction fee</strong> charged only to the hiring company.</span></li>
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" /><span>The fee is <strong>€1 per hour per worker</strong> <em>or</em> <strong>3% of the booking value</strong>, whichever is lower.</span></li>
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" /><span>The lower fee is <strong>calculated automatically</strong> at booking time — no negotiation required.</span></li>
                <li className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" /><span>No subscriptions. No listing fees. You pay only when you book.</span></li>
              </ul>
            </div>
          </div>

          <div className="mt-8 rounded-xl p-6 bg-navy-900 text-white text-center">
            <Sparkles className="w-5 h-5 text-brand mx-auto mb-3" />
            <ReadMore
              className="max-w-3xl mx-auto !text-white/85 text-center"
              preview="We keep the fee model intentionally low and transparent."
              more="SecuSource was built by experienced security professionals to strengthen the industry, create better opportunities for professionals and provide greater value to companies. The economics have to reflect that."
              buttonMoreLabel="Why"
              testId="about-manifesto-readmore"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16 text-center">
        <h2 className="text-3xl font-outfit font-bold text-white tracking-tight">Ready to raise the standard?</h2>
        <p className="text-white/70 mt-3 font-manrope max-w-2xl mx-auto">
          Whether you deploy security personnel or you are the professional being deployed — SecuSource was built for you.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
          <Button asChild size="lg" className="bg-brand hover:bg-brand-hover text-white px-6 py-6" data-testid="about-cta-pro">
            <Link to="/register/staff">Join as a Professional — Free</Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-brand text-brand hover:bg-brand-light px-6 py-6" data-testid="about-cta-company">
            <Link to="/register/company">Register as a Company</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
